'use client';

import { useLocale } from 'next-intl';
import { useRouter, usePathname } from '@medizzconnect/i18n';
import { locales, localeNames, localeFlags, type Locale } from '@medizzconnect/i18n';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@medizzconnect/auth';
import { cn } from '@medizzconnect/shared';

export function LanguageSwitcher() {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const { isSignedIn } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const handleSwitch = useCallback(async (newLocale: Locale) => {
    setIsOpen(false);
    // Eingeloggte Nutzer: Sprache in DB speichern, damit LocaleRedirect nicht zurücksetzt
    if (isSignedIn) {
      try {
        await Promise.all([
          fetch('/api/profile', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ preferred_language: newLocale }),
          }),
          fetch('/api/profile', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'video_profile', language: newLocale }),
          }),
        ]);
      } catch { /* Fehler ignorieren — Navigation trotzdem durchführen */ }
    }
    router.replace(pathname, { locale: newLocale });
  }, [isSignedIn, pathname, router]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium',
          'text-text-secondary hover:text-text-primary hover:bg-bg-subtle',
          'transition-colors duration-150',
        )}
        aria-label="Sprache wechseln"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-base" aria-hidden="true">{localeFlags[locale]}</span>
        <span className="hidden sm:inline">{locale.toUpperCase()}</span>
        <svg
          className={cn('h-3.5 w-3.5 transition-transform duration-150', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-border-light bg-white py-1 shadow-lg z-[1010]"
          role="listbox"
          aria-label="Sprachauswahl"
        >
          {locales.map((l) => (
            <button
              key={l}
              onClick={() => handleSwitch(l)}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                l === locale
                  ? 'bg-primary-50 text-primary-600 font-medium'
                  : 'text-text-primary hover:bg-bg-subtle',
              )}
              role="option"
              aria-selected={l === locale}
            >
              <span className="text-base">{localeFlags[l]}</span>
              <span>{localeNames[l]}</span>
              {l === locale && (
                <svg className="ml-auto h-4 w-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
